import { CHUNK_ERROR_PATTERN } from 'lib/telemetry/chunkReloadScript';

/**
 * Counts of recent failures for the blank-content watchdog to attach to its
 * report: "blank, and 3 chunk loads failed in the last 30s" points somewhere
 * very different from "blank, nothing failed". Counts only — no messages,
 * URLs or stacks are kept.
 */

export type FailureKind = 'error' | 'chunk_load' | 'rsc_fetch' | 'auto_recovery' | 'recovery_blocked';

export type FailureCounts = Partial<Record<FailureKind, number>>;

const RECENT_WINDOW_MS = 30_000;
const MAX_RECENT = 100;

const recent: Array<{ kind: FailureKind; at: number }> = [];
const totals: FailureCounts = {};

/** Next logs this when a client navigation's RSC fetch fails, then falls back to a full page load. */
export const RSC_FETCH_FAILURE_MESSAGE = 'Failed to fetch RSC payload';

export function noteFailure(kind: FailureKind): void {
    totals[kind] = (totals[kind] ?? 0) + 1;
    if (recent.length >= MAX_RECENT) recent.shift();
    recent.push({ kind, at: performance.now() });
}

/** Classifies a RUM error message into the bucket it is counted under. */
export function classifyErrorMessage(message: string | undefined): FailureKind {
    if (message?.includes(RSC_FETCH_FAILURE_MESSAGE)) return 'rsc_fetch';
    if (CHUNK_ERROR_PATTERN.test(message ?? '')) return 'chunk_load';
    return 'error';
}

export function getFailureCounts(now = performance.now()): { recent: FailureCounts; total: FailureCounts } {
    const recentCounts: FailureCounts = {};
    for (const entry of recent) {
        if (now - entry.at <= RECENT_WINDOW_MS) {
            recentCounts[entry.kind] = (recentCounts[entry.kind] ?? 0) + 1;
        }
    }
    return { recent: recentCounts, total: { ...totals } };
}
